import React, { memo, useCallback, useState } from 'react';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import { MY_DAY_CONFIRMATION_KEY } from '../../../../common/constants';
import { MY_DAY_STEP_STATUS } from '../../../../common/utils/myDay/driverDaySteps';
import Text from '../../../components/text/Text';
import { ActionDock, Card, EmptyState, Screen, TickRow } from '../../../design/components';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { useDriverDay } from '../DriverDayProvider';
import AccessItemStops from '../AccessItemStops';
import DriverTrainingBanner from '../DriverTrainingBanner';
import { RUN_LOAD_ERROR } from '../driverRoutes';
import useDriverDayActions from '../useDriverDayActions';
import StepCount from './StepCount';
import StepNote from './StepNote';
import { getStopLabels } from './stopLabels';
import useEndOfDaySteps, { RETURN_KEYS_STEP_KEY } from './useEndOfDaySteps';

const ReturnItemRow = memo(({ item, checked, disabled, onToggle }) => {
	const handleToggle = useCallback(nextChecked => onToggle(item.tickKeys, nextChecked), [onToggle, item.tickKeys]);

	return (
		<TickRow
			overline={item.kind}
			title={item.title}
			checked={checked}
			disabled={disabled}
			onToggle={handleToggle}
		/>
	);
});

const ReturnStopCard = memo(({ stop, isTakenOut, tickedByKey, disabled, onToggle }) => {
	const styles = useThemedStyles(createStyles);
	const { site, position, machineNumber } = getStopLabels(stop.docket);
	const showStopHeading = stop.items.every(item => item.stops.length === 1);

	return (
		<Card style={styles.stopCard}>
			{showStopHeading && <View>
				<Text style={styles.site}>{site || position || 'Unnamed stop'}</Text>
				{Boolean(position || machineNumber) && (
					<Text style={styles.position}>{[site ? position : null, machineNumber].filter(Boolean).join(' · ')}</Text>
				)}
				{isTakenOut && <Text style={styles.caption}>Taken out at Start of day</Text>}
			</View>}
			{!showStopHeading && isTakenOut && <Text style={styles.caption}>Taken out at Start of day</Text>}
			<View style={styles.items}>
				{stop.items.map(item => (
					<View key={item.id} style={styles.items}>
						<ReturnItemRow
							item={item}
							checked={item.tickKeys.every(key => Boolean(tickedByKey[key]))}
							disabled={disabled}
							onToggle={onToggle}
						/>
						{!showStopHeading && <AccessItemStops stops={item.stops} />}
					</View>
				))}
			</View>
		</Card>
	);
});

/**
 * Return keys and access passes (End of day): the keys, passes and PPE that
 * the run's stops needed (same data as Keys and site access), one tick each.
 * "Keys and passes returned" is the returnKeys My day step (offline-queued like
 * the other end-of-day confirmations), so managers see it in the day preview.
 */
const ReturnKeysView = () => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const day = useDriverDay();
	const {
		steps,
		getStepAfter,
		getStepPosition,
		returnKeyStops,
		areKeysReturned,
	} = useEndOfDaySteps(day);
	const goBack = useCallback(() => navigation.goBack(), [navigation]);
	const { confirmStepByKey, isConfirmingStep } = useDriverDayActions(day, { onStepConfirmed: goBack });
	const [tickedByKey, setTickedByKey] = useState({});

	const hasStep = steps.some(item => item.key === RETURN_KEYS_STEP_KEY);
	const isTakenOut = day.stepByKey.accessKeys?.status === MY_DAY_STEP_STATUS.DONE;
	const items = returnKeyStops.flatMap(stop => stop.items);
	const allTickKeys = items.flatMap(item => item.tickKeys);
	// Once confirmed, show what was saved (on any phone); older confirmations have no list.
	const savedItems = day.serviceRun?.myDayConfirmations
		?.find(confirmation => confirmation?.key === MY_DAY_CONFIRMATION_KEY.RETURN_KEYS)?.items;
	const displayedTicks = areKeysReturned
		? Object.fromEntries(allTickKeys.map(key => [key, !Array.isArray(savedItems) || savedItems.includes(key)]))
		: tickedByKey;
	const tickedCount = items.filter(item => item.tickKeys.every(key => displayedTicks[key])).length;

	const toggle = useCallback((tickKeys, nextChecked) => {
		setTickedByKey(previous => ({ ...previous, ...Object.fromEntries(tickKeys.map(key => [key, nextChecked])) }));
	}, []);
	// Sends the ticked ids (getRunAccessItems format) so managers see what was not returned.
	const confirm = useCallback(() => (
		confirmStepByKey(RETURN_KEYS_STEP_KEY, allTickKeys.filter(key => tickedByKey[key]))
	), [allTickKeys, confirmStepByKey, tickedByKey]);

	const renderDock = () => {
		if (!hasStep) {
			return null;
		}
		if (areKeysReturned) {
			return <ActionDock action={{ label: 'Back to end of day', onPress: () => navigation.goBack() }} />;
		}

		const stepAfter = getStepAfter(RETURN_KEYS_STEP_KEY);
		return (
			<ActionDock
				hint={tickedCount === items.length && stepAfter
					? `After this: ${stepAfter.label}`
					: `${tickedCount} of ${items.length} ${items.length === 1 ? 'item' : 'items'} returned`}
				action={{ label: 'Keys and passes returned', onPress: confirm, loading: isConfirmingStep }}
			/>
		);
	};

	return (
		<Screen
			backLabel="End of day"
			headerRight={<StepCount position={getStepPosition(RETURN_KEYS_STEP_KEY)} />}
			heading="Return keys and access passes"
			subheading="Hand back everything you took out for today's stops."
			isLoading={day.isLoading}
			error={day.loadError ? RUN_LOAD_ERROR : undefined}
			onRetry={day.refresh}
			banner={<DriverTrainingBanner />}
			dock={renderDock()}
		>
			{
				hasStep ? (
					<>
						{areKeysReturned && <StepNote tone="success" text="Keys and passes returned." />}
						<View style={styles.stops}>
							{returnKeyStops.map(stop => (
								<ReturnStopCard
									key={stop.docket.id}
									stop={stop}
									isTakenOut={isTakenOut}
									tickedByKey={displayedTicks}
									disabled={areKeysReturned}
									onToggle={toggle}
								/>
							))}
						</View>
						<Text style={styles.footnote}>Return keys and passes to where your warehouse keeps them.</Text>
						{!areKeysReturned && <StepNote tone="warning" text="Something missing? Tell your manager before you clock out." />}
					</>
				) : (
					<EmptyState title="Nothing to return" message="None of today's stops needed keys, access passes or PPE." />
				)
			}
		</Screen>
	);
};

const createStyles = ({ ui: { colors, space, type } }) => ({
	stops: {
		gap: space.md,
	},
	stopCard: {
		gap: 10,
		padding: 14,
	},
	site: {
		...type.sectionTitle,
		color: colors.text,
	},
	position: {
		...type.bodySmallStrong,
		color: colors.text,
		marginTop: 2,
	},
	caption: {
		...type.caption,
		color: colors.textSecondary,
		marginTop: 2,
	},
	items: {
		gap: space.sm,
	},
	footnote: {
		...type.caption,
		color: colors.textSecondary,
		paddingHorizontal: space.xs,
	},
});

export default ReturnKeysView;
